import React from 'react';
import { Card } from './Card';
import { ScoreBadge, LiftBadge } from './Signals';
import { compact, platformLabel, platformSeries, timeAgo } from '../../utils/platforms';
import { cn } from './cn';

/**
 * One post, wherever a post appears: the dashboard's standout lists, the
 * all-posts timeline, and each account page's ranked posts.
 *
 * It was three separate renderings before, which is how the timeline gained a
 * thumbnail fallback rule and a metrics line that the dashboard never got.
 * One component means a post reads the same everywhere, and a fix to how a
 * caption truncates happens once.
 *
 * `compact` is a density, not a different design: the dashboard shows six of
 * these under a heading and needs one line each, the timeline shows the post
 * itself and can afford three. Same fields, same order, same badges.
 */
export const PostCard = ({ post, compact: dense = false, badge, className }) => {
  const when = timeAgo(post.published_at);
  const linked = !!post.permalink;
  // Set by the server when the post's files were a template's export. Text,
  // never a link: the whole card is already a link to the post, and a link
  // inside a link is not something a tap can choose between.
  const template = post.template?.name || null;

  const meta = (
    <span className={cn(
      'flex min-w-0 items-center gap-x-2 text-caption text-ink-subtle',
      dense && 'mt-0.5'
    )}>
      <span className="inline-flex shrink-0 items-center gap-1.5">
        {/* The dot is identity alongside the name, never instead of it: the
            series palette clears the colour-blind target only because the
            label is always beside it. */}
        <span aria-hidden="true" className="size-1.5 rounded-pill"
              style={{ background: platformSeries(post.platform) }} />
        {platformLabel(post.platform)}
      </span>
      {post.username && <span className="truncate">@{post.username}</span>}
      {when && <span className="shrink-0">{when}</span>}
      {dense && template && (
        <span className="shrink-0 rounded-pill border border-line px-1.5 text-caption-sm text-ink-muted"
              title={`Made with the template “${template}”`} data-post-template={template}>
          Template
        </span>
      )}
    </span>
  );

  return (
    <Card
      as={linked ? 'a' : 'div'}
      href={post.permalink || undefined}
      target={linked ? '_blank' : undefined}
      rel={linked ? 'noreferrer' : undefined}
      interactive={linked}
      size={dense ? 'sm' : 'md'}
      className={cn('block min-w-0', className)}
    >
      <div className="flex min-w-0 gap-3">
        {/* No placeholder square when there is no thumbnail: three of the five
            platforms are text-first, and an empty box takes a sixth of the
            row's width to say nothing. */}
        {post.thumbnail_url && (
          <img
            src={post.thumbnail_url}
            alt=""
            loading="lazy"
            className={cn(
              'shrink-0 rounded-field bg-surface-sunken object-cover',
              dense ? 'size-10' : 'size-14 sm:size-16'
            )}
          />
        )}

        <div className="min-w-0 flex-1">
          {!dense && meta}
          <p className={cn(
            'text-body-sm',
            dense ? 'truncate text-ink' : 'mt-1 line-clamp-2 text-ink-body'
          )}>
            {post.caption?.trim() || 'Untitled post'}
          </p>
          {dense && meta}

          {!dense && (
            /* One line. The labels stay — "240k · 31k" is shorter but
               unreadable — so shares drops below `sm` instead: all four
               labelled metrics need ~310px and a phone row has ~278px, which
               was cutting the last one mid-word ("2.4k sha"). */
            <div className="mt-1.5 flex min-w-0 items-center gap-x-3 truncate text-caption text-ink-muted tabular-nums">
              {post.views != null && <span className="shrink-0">{compact(post.views)} views</span>}
              {post.likes != null && <span className="shrink-0">{compact(post.likes)} likes</span>}
              {post.comments != null && <span className="shrink-0">{compact(post.comments)} comments</span>}
              {post.shares != null && (
                <span className="hidden shrink-0 sm:inline">{compact(post.shares)} shares</span>
              )}
            </div>
          )}
          {!dense && template && (
            <p className="mt-1 flex min-w-0 gap-1 text-caption text-ink-subtle" data-post-template={template}>
              <span className="shrink-0">Made with</span>
              <span className="min-w-0 truncate text-ink-muted">{template}</span>
            </p>
          )}
        </div>

        <div className={cn(
          'flex shrink-0',
          dense ? 'items-center' : 'flex-col items-end gap-1.5'
        )}>
          {badge !== undefined ? badge : (
            <>
              <ScoreBadge score={post.score} />
              {!dense && <LiftBadge lift={post.lift} tier={post.lift_tier} compactLabel />}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default PostCard;
